import { AlertColor, Avatar, Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import axiosInstance from '../../../utils/axios';
import { getTableAvatarSx } from '../../../utils/avatarColors';
import { images } from '../../../utils/constants/images';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import ListPage from '../CommonPage';
import AddEditCustomer from './AddEditCustomer/AddEditCustomer';

interface CustomerListProps {
  companyId?: number;
  branchId?: number;
  customEdit?: (id: number | string | any) => void;
  customAdd?: () => void;
  onRefreshCallback?: (refreshFn: () => void) => void;
  companyName?: string;
  branchData?: {
    country?: number;
    state?: number;
    city?: number;
    address?: string;
    states?: any[];
    cities?: any[];
  };
}

const getCustomerInitials = (row: any) => {
  const first = String(row?.firstName || '').trim();
  const last = String(row?.lastName || '').trim();
  if (first || last) {
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    if (initials.trim()) return initials;
  }
  const fullName = String(row?.fullName || '').trim();
  if (!fullName) return '?';
  const parts = fullName.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const formatCustomerName = (row: any) => {
  const fullName = String(row?.fullName || '').trim();
  if (fullName) return fullName;
  return [row?.firstName, row?.lastName].filter(Boolean).join(' ').trim() || '—';
};

const DualLineCell: FC<{ primary?: string; secondary?: string }> = ({ primary, secondary }) => (
  <Box sx={{ py: 0.25 }}>
    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, color: '#101828' }}>
      {primary || '—'}
    </Typography>
    {secondary ? (
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>{secondary}</Typography>
    ) : null}
  </Box>
);

const CustomerList: FC<CustomerListProps> = ({
  companyId,
  branchId,
  customEdit,
  customAdd,
  onRefreshCallback,
  companyName,
  branchData,
}) => {
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const index = useSelector(pageNo);
  const [pageResponse, setPageResponse] = useState<{ content: any[] }>({ content: [] });
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<number | string | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const usesExternalDrawer = Boolean(customAdd || customEdit);

  const resetPassword = async (id: number) => {
    try {
      setLoading(true);
      const { status } = await axiosInstance.put(URLS.resetUser(id));
      if (status === 200) {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.password_reset_success_message,
        });
      }
    } catch (error: any) {
      setLoading(false);
    }
  };

  const toggleUser = async (id: number) => {
    try {
      setLoading(true);
      const { status } = await axiosInstance.put(URLS.toggleUser(id));
      if (status === 200) {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.status_change_success_message,
        });
        getList(null);
      }
    } catch (error: any) {
      setLoading(false);
    }
  };

  const openAddDrawer = () => {
    if (customAdd) {
      customAdd();
      return;
    }
    setEditingCustomerId(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (customerId: number | string) => {
    if (customEdit) {
      const rowData: any = pageResponse.content.find((item: any) => item.id === customerId);
      if (rowData) {
        customEdit(rowData);
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: 'Customer not found',
        });
      }
      return;
    }
    setEditingCustomerId(customerId);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingCustomerId(null);
  };

  const handleDrawerSave = () => {
    closeDrawer();
    getList(null);
  };

  const pageConfig = useMemo(
    () => ({
      title: 'Customers',
      entityLabel: 'Customers',
      subtitle: 'Manage customers, contact details, and booking history.',
      searchField: 'search',
      searchPlaceholder: 'Search by name, email, phone, or company',
      AddButtonText: strings.customermgmt_button_add,
      customAdd: openAddDrawer,
      customEdit: openEditDrawer,
      onRowClick: (item: any) => {
        if (item?.id != null) openEditDrawer(item.id);
      },
      listApi: URLS.users(true),
      deleteApi: URLS.deleteCustomer,
      addPrivilege: privileges.add_customer,
      editPrivilege: privileges.edit_customer,
      deletePrivilege: privileges.delete_customer,
      blockUserPrivilege: privileges.block_customer,
      resetPasswordPrivilege: privileges.reset_password_customer,
      exportPrivilege: privileges.export_customers,
      exportIconOnly: true,
      exportApi: branchId ? `${URLS.exportCustomers}?branchId=${branchId}` : URLS.exportCustomers,
      customImage: {
        imageUrl: images.resetPasswordIcon,
        handleCustomImageMethod: resetPassword,
        privilege: privileges.reset_password_customer,
        tooltip: 'Reset',
      },
      toggleUser,
      ...(companyId && branchId
        ? {
            importPrivilege: privileges.add_customer,
            importApi: URLS.importCompanyBranchCustomers(companyId, branchId),
            downloadImportTemplate: URLS.downloadCompanyBranchCustomersTemplate(companyId, branchId),
          }
        : {}),
      tableColumn: [
        {
          field: 'fullName',
          headerName: 'Customer',
          flex: 1.6,
          sortable: true,
          localField: 'firstName',
          renderCell: (_value: any, row: any) => {
            const name = formatCustomerName(row);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25 }}>
                <Avatar sx={getTableAvatarSx(name)}>{getCustomerInitials(row)}</Avatar>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#101828', lineHeight: 1.35 }}>
                  {name}
                </Typography>
              </Box>
            );
          },
        },
        {
          field: 'email',
          headerName: 'Contact',
          flex: 1.4,
          sortable: true,
          renderCell: (_value: any, row: any) => {
            const email = row.email?.trim();
            const phone = [row.countryCode, row.phone].filter(Boolean).join(' ').trim() || row.phone?.trim();
            if (!email && !phone) return '—';
            return <DualLineCell primary={email || '—'} secondary={phone || undefined} />;
          },
        },
        ...(branchId
          ? []
          : [
              {
                field: 'companyName',
                headerName: 'Company',
                flex: 1.2,
                sortable: true,
                renderCell: (value: any) => value || '—',
              },
              {
                field: 'lastBookingDate',
                headerName: 'Last Booking Date',
                flex: 1.1,
                sortable: false,
                renderCell: (value: any) => (value ? dayjs(value).format('D MMM YYYY') : '—'),
              },
            ]),
      ],
    }),
    [branchId, companyId, pageResponse.content],
  );

  const getList = useCallback(
    async (filterData: any) => {
      try {
        setLoading(true);
        const body = filterData
          ? filterData
          : { page: index + 1, size: pageSize, filter: filter, sort: ['createdAt:desc'] };

        let apiUrl = pageConfig.listApi;
        if (branchId) {
          apiUrl = `${apiUrl}${apiUrl.includes('?') ? '&' : '?'}branchId=${branchId}`;
        }

        const { data } = await axiosInstance.post(apiUrl, body);
        setPageResponse({
          ...data,
          content: Array.isArray(data.content) ? data.content : [],
        });
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
      }
    },
    [pageConfig.listApi, pageSize, filter, branchId, index],
  );

  useEffect(() => {
    (async () => {
      await getList(null);
    })();
  }, [getList]);

  useEffect(() => {
    if (onRefreshCallback) {
      onRefreshCallback(() => getList(null));
    }
  }, [onRefreshCallback, getList]);

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}

      <ListPage pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />

      {!usesExternalDrawer ? (
        <AddEditCustomer
          open={drawerOpen}
          onClose={closeDrawer}
          handleSave={handleDrawerSave}
          editingCustomerId={editingCustomerId}
          companyId={companyId}
          branchId={branchId}
          companyName={companyName}
          branchData={branchData}
        />
      ) : null}
    </>
  );
};

export default CustomerList;
